import { apiPath } from '../../constants';
import {
    axios,
    apiRequestOptions,
    transformHttpResponse,
} from 'utils';

export const SHOOTING_RESULT_SAVE_SUCCESS = 'SHOOTING_RESULT_SAVE_SUCCESS';
export const SHOOTING_RESULT_REMOVE_SUCCESS = 'SHOOTING_RESULT_REMOVE_SUCCESS';

/**
 * Create (no id) or update (existing id) one athlete's shooting score — or,
 * with `didNotShoot`, the «Не стріляв» mark instead of a score (16 Oct 2026).
 *
 * Returns a promise the caller awaits for per-row save state; the resolved
 * result is also upserted into the shootingResults store. Rejection carries
 * the backend violation message when present — the row displays it inline.
 */
export const saveShootingResult = ({ competitionId, sportsmanId, shootingScore, didNotShoot = false, existingId = null }) => {
    return function(dispatch) {
        const body = didNotShoot ? { didNotShoot: true } : { shootingScore };
        const options = existingId
            ? apiRequestOptions(apiPath.SHOOTING_RESULTS.PATCH, body, { params: { id: existingId } })
            : apiRequestOptions(apiPath.SHOOTING_RESULTS.POST, {
                competition: '/api/competitions/' + competitionId,
                sportsman: '/api/sportsmen/' + sportsmanId,
                ...body,
            });

        const saved = result => {
            dispatch({ type: SHOOTING_RESULT_SAVE_SUCCESS, result, competitionId });
            return result;
        };

        return axios(options)
            .then(transformHttpResponse)
            .then(response => saved(response.data))
            .catch(error => {
                // ALREADY THERE (24 Oct 2026: «спортсмен вже має результат зі
                // стрільби у цих змаганнях» on a row that showed none). The
                // page did not know of a result the server holds — a list read
                // that overlapped somebody's save, answered from a stale cache.
                // Read THIS athlete's result afresh and write the value into it.
                if (existingId || !isAlreadyEntered(error)) {
                    return rejectWithViolationMessage(error);
                }

                return axios(apiRequestOptions(apiPath.SHOOTING_RESULTS.LIST, {
                    competition: competitionId,
                    sportsman: sportsmanId,
                    // Its own address, so no cached copy of a list answers it.
                    fresh: Date.now(),
                }))
                    .then(transformHttpResponse)
                    .then(response => {
                        const rows = Array.isArray(response.data) ? response.data : (response.data['hydra:member'] || []);
                        const found = rows[0];
                        if (!found) {
                            return rejectWithViolationMessage(error);
                        }

                        return axios(apiRequestOptions(apiPath.SHOOTING_RESULTS.PATCH, body, { params: { id: found.id } }))
                            .then(transformHttpResponse)
                            .then(patched => saved(patched.data));
                    })
                    .catch(rejectWithViolationMessage);
            });
    }
};

/** The server refused a second result for this athlete in this competition. */
const isAlreadyEntered = error => {
    const response = error && error.response;
    const violations = response && response.data && response.data.violations;

    return Boolean(response && 422 === response.status && violations
        && violations.some(v => 'sportsman' === v.propertyPath));
};

/**
 * Take the «Не стріляв» mark off an athlete who has no score: the row goes,
 * and the athlete is back to «not entered yet».
 */
export const removeShootingResult = ({ competitionId, id }) => {
    return function(dispatch) {
        return axios(apiRequestOptions(apiPath.SHOOTING_RESULTS.DELETE, null, { params: { id } }))
            .then(() => {
                dispatch({ type: SHOOTING_RESULT_REMOVE_SUCCESS, id, competitionId });
            })
            .catch(rejectWithViolationMessage);
    }
};

const rejectWithViolationMessage = error => {
    if (error instanceof Error && !error.response) {
        return Promise.reject(error);
    }
    const violations = error.response && error.response.data && error.response.data.violations;
    const message = (violations && violations.length && violations[0].message)
        || (error.response && error.response.data && error.response.data['hydra:description'])
        || (error.response && error.response.data && error.response.data.message)
        || 'Save failed';
    return Promise.reject(new Error(message));
};
